import React, { useState, useMemo } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { getLogs } from "../api";

export const Route = createFileRoute("/SiteHits")({
  component: SiteHits,
});

type LogEntry = Record<string, string>;

function parseLog(raw: string): LogEntry | null {
  if (!raw.trim()) return null;
  const parts = raw.split(" | ").map(p => p.trim());
  const entry: LogEntry = {};
  entry.timestamp = parts.shift() ?? "";
  for (const part of parts) {
    const [key, ...rest] = part.split("=");
    if (key) {
      entry[key.toLowerCase()] = rest.join("=");
    }
  }
  return entry;
}

export function SiteHits() {
  const [logs, setLogs] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [password, setPassword] = useState("");
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [error, setError] = useState("");
  
  // Filter state
  const [searchTerm, setSearchTerm] = useState("");

  const handleFetchLogs = (e?: React.FormEvent) => {
    if (e) e.preventDefault();
    isAuthenticated ? setRefreshing(true) : setLoading(true);
    setError("");

    getLogs(password)
      .then((data) => {
        setLogs(data);
        setIsAuthenticated(true);
      })
      .catch(() => setError("Invalid password or session expired."))
      .finally(() => {
        setLoading(false);
        setRefreshing(false);
      });
  };

  const parsedLogs = useMemo(() => {
    return logs
      .map(parseLog)
      .filter((l): l is LogEntry => l !== null);
  }, [logs]);

  // Logic to filter by any property value
  const filteredLogs = useMemo(() => {
    if (!searchTerm.trim()) return parsedLogs;
    const term = searchTerm.toLowerCase();
    return parsedLogs.filter((log) => 
      Object.values(log).some(val => val.toLowerCase().includes(term))
    );
  }, [parsedLogs, searchTerm]);

  const containerStyle: React.CSSProperties = {
    padding: "20px",
    fontFamily: "monospace",
    backgroundColor: "#000",
    color: "#fff",
    minHeight: "100vh",
    width: "100vw",
    boxSizing: "border-box",
    margin: 0,
  };

  if (!isAuthenticated) {
    return (
      <div style={{ ...containerStyle, display: "flex", justifyContent: "center", alignItems: "center" }}>
        <form onSubmit={handleFetchLogs} style={loginBox}>
          <h2 style={{ marginTop: 0 }}>LOG_VIEWER_AUTH</h2>
          <input
            type="password"
            placeholder="ACCESS_KEY"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            style={input}
          />
          <button type="submit" disabled={loading} style={button}>
            {loading ? "VERIFYING..." : "ACCESS_LOGS"}
          </button>
          {error && <p style={{ color: "#ff4d4d", marginTop: "15px" }}>{error}</p>}
        </form>
      </div>
    );
  }

  return (
    <div style={containerStyle}>
      <header style={header}>
        <div>
          <h2 style={{ margin: 0 }}>SYSTEM_TRAFFIC_LOGS</h2>
          <div style={{ fontSize: "12px", color: "#666", marginTop: "5px" }}>
            SHOWING {filteredLogs.length} OF {parsedLogs.length} ENTRIES
          </div>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
          <input 
            type="text"
            placeholder="FILTER BY ANY PROPERTY (IP, CITY, UA...)"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            style={filterInput}
          />
          <button onClick={() => handleFetchLogs()} disabled={refreshing} style={smallButton}>
            {refreshing ? "REFRESHING..." : "REFRESH"}
          </button>
          <button
            onClick={() => { setIsAuthenticated(false); setPassword(""); setSearchTerm(""); }}
            style={{ ...smallButton, color: "#ff4d4d" }}
          >
            LOGOUT
          </button>
        </div>
      </header>

      <div style={grid}>
        {filteredLogs.map((log, i) => (
          <div key={i} style={card}>
            <div style={muted}>{log.timestamp}</div>
            
            <div style={groupTitle}>[ TRAFFIC ]</div>
            <div style={section}><span style={label}>IP:</span> {log.ip}</div>
            <div style={section}><span style={label}>PATH:</span> {log.path}</div>
            <div style={section}><span style={label}>SOURCE:</span> {log.source || "null"}</div>
            <div style={section}><span style={label}>UTM_SRC:</span> {log.utm_source || "null"}</div>
            <div style={section}><span style={label}>UTM_CAM:</span> {log.utm_campaign || "null"}</div>

            <div style={groupTitle}>[ LOCATION ]</div>
            <div style={section}><span style={label}>COUNTRY:</span> {log.country}</div>
            <div style={section}><span style={label}>REGION:</span> {log.region}</div>
            <div style={section}><span style={label}>CITY:</span> {log.city}</div>

            <div style={groupTitle}>[ NETWORK ]</div>
            <div style={section}><span style={label}>ISP:</span> {log.isp}</div>
            <div style={section}><span style={label}>ASN:</span> {log.asn}</div>
            <div style={section}><span style={label}>ORG:</span> {log.org}</div>

            <div style={groupTitle}>[ DEVICE & SECURITY ]</div>
            <div style={section}><span style={label}>MOBILE:</span> {log.mobile}</div>
            <div style={section}><span style={label}>PROXY:</span> {log.proxy}</div>
            <div style={section}><span style={label}>HOSTING:</span> {log.hosting}</div>
            <div style={section}>
              <span style={label}>UA:</span> 
              <span style={{ fontSize: "10px", color: "#888" }}> {log.ua}</span>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

/* STYLES */

const loginBox: React.CSSProperties = {
  width: "100%",
  maxWidth: "400px",
  padding: "40px",
  background: "#0a0a0a",
  border: "1px solid #333",
  textAlign: "center",
};

const input: React.CSSProperties = {
  width: "100%",
  padding: "12px",
  marginBottom: "20px",
  background: "#000",
  border: "1px solid #444",
  color: "#fff",
  boxSizing: "border-box",
  fontFamily: "monospace",
};

const filterInput: React.CSSProperties = {
  padding: "8px 12px",
  background: "#000",
  border: "1px solid #333",
  color: "#fff",
  fontFamily: "monospace",
  fontSize: "12px",
  width: "300px",
  outline: "none",
};

const button: React.CSSProperties = {
  width: "100%",
  padding: "12px",
  background: "#fff",
  color: "#000",
  border: "none",
  cursor: "pointer",
  fontWeight: "bold",
};

const smallButton: React.CSSProperties = {
  background: "#111",
  border: "1px solid #444",
  color: "#fff",
  padding: "8px 16px",
  cursor: "pointer",
  fontSize: "12px",
};

const header: React.CSSProperties = {
  display: "flex",
  justifyContent: "space-between",
  alignItems: "flex-start",
  borderBottom: "1px solid #333",
  paddingBottom: "20px",
  marginBottom: "20px",
};

const grid: React.CSSProperties = {
  display: "grid",
  gridTemplateColumns: "repeat(auto-fill, minmax(400px, 1fr))",
  gap: "20px",
  width: "100%",
};

const card: React.CSSProperties = {
  border: "1px solid #222",
  background: "#050505",
  padding: "20px",
  borderRadius: "4px",
};

const groupTitle: React.CSSProperties = {
  fontSize: "11px",
  color: "#4da3ff",
  marginTop: "15px",
  marginBottom: "5px",
  borderBottom: "1px solid #111"
};

const section: React.CSSProperties = {
  marginTop: "4px",
  fontSize: "12px",
  wordBreak: "break-all",
};

const label: React.CSSProperties = {
  color: "#666",
  display: "inline-block",
  width: "80px"
};

const muted: React.CSSProperties = {
  fontSize: "11px",
  color: "#444",
};

export default SiteHits;